import { useState, useEffect } from 'react';
import { api, type EmailPreview, type ParsedInvoicePreview } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Mail, Loader2, Check, X, Circle, FileText, AlertCircle, RefreshCw, SkipForward } from 'lucide-react';

interface GmailSyncModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

type WizardStep =
  | 'idle'
  | 'connecting'
  | 'fetching'
  | 'extracting'
  | 'review'
  | 'confirming'
  | 'done'
  | 'error';

// Datos de una factura existente (duplicada)
interface ExistingInvoiceInfo {
  fecha_emision: string;
  valor_total: number;
  estado: string;
}

// Accion del usuario para cada duplicado: ignorar o reemplazar
type DuplicateAction = 'ignore' | 'replace';

interface WizardState {
  step: WizardStep;
  connectedEmail: string | null;
  emails: EmailPreview[];
  emailCount: number;
  attachmentsCount: number;
  invoices: ParsedInvoicePreview[];
  extractErrors: Array<{ error: string }>;
  selectedIndices: Set<number>;
  markAsRead: boolean;
  created: Array<{ facturaId?: string; numero?: string; proveedor?: string; valorTotal?: number }>;
  duplicates: Array<{ numero?: string; error?: string }>;
  confirmErrors: Array<{ error?: string }>;
  errorMessage: string | null;
  // Duplicados detectados: numero -> info de la factura existente
  detectedDuplicates: Record<string, ExistingInvoiceInfo>;
  // Accion elegida por el usuario para cada duplicado
  duplicateActions: Record<string, DuplicateAction>;
}

const initialState: WizardState = {
  step: 'idle',
  connectedEmail: null,
  emails: [],
  emailCount: 0,
  attachmentsCount: 0,
  invoices: [],
  extractErrors: [],
  selectedIndices: new Set(),
  markAsRead: true,
  created: [],
  duplicates: [],
  confirmErrors: [],
  errorMessage: null,
  detectedDuplicates: {},
  duplicateActions: {},
};

interface StepInfo {
  label: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  result?: string;
}

function buildSteps(state: WizardState): StepInfo[] {
  if (state.step === 'idle') return [];

  const steps: StepInfo[] = [];
  const s: WizardStep = state.step;

  // Step 1: Conectando
  const step1Completed = !!state.connectedEmail;
  steps.push({
    label: 'Conectando a Gmail...',
    status: s === 'connecting' ? 'active'
      : step1Completed ? 'completed'
      : s === 'error' ? 'error'
      : 'pending',
    result: state.connectedEmail ? `Conectado como ${state.connectedEmail}` : undefined,
  });

  // Step 2: Obteniendo correos
  const step2Done = step1Completed && s !== 'connecting' && s !== 'fetching';
  steps.push({
    label: 'Obteniendo correos...',
    status: s === 'fetching' ? 'active'
      : step2Done ? 'completed'
      : 'pending',
    result: step2Done ? `${state.emailCount} correo(s) encontrado(s)` : undefined,
  });

  // Step 3: Buscando adjuntos y extrayendo facturas
  if (state.emailCount > 0 || s === 'extracting') {
    const step3Done = step2Done && s !== 'extracting';
    steps.push({
      label: 'Buscando adjuntos y extrayendo facturas...',
      status: s === 'extracting' ? 'active'
        : step3Done ? 'completed'
        : s === 'error' && state.emailCount > 0 ? 'error'
        : 'pending',
      result: step3Done
        ? `${state.attachmentsCount} adjunto(s), ${state.invoices.length} factura(s)` : undefined,
    });
  }

  // Step 4: Importando (solo después de review)
  if (s === 'confirming' || (s === 'done' && state.created.length + state.duplicates.length + state.confirmErrors.length > 0)) {
    steps.push({
      label: 'Importando facturas...',
      status: s === 'confirming' ? 'active' : 'completed',
      result: s === 'done'
        ? `${state.created.length} creada(s), ${state.duplicates.length} duplicada(s)` : undefined,
    });
  }

  return steps;
}

function WizardStepIndicator({ steps }: { steps: StepInfo[] }) {
  if (steps.length === 0) return null;

  return (
    <div className="space-y-0">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={cn(
              "flex items-center justify-center w-7 h-7 rounded-full border-2",
              step.status === 'completed' && "bg-green-100 border-green-500",
              step.status === 'active' && "bg-blue-100 border-blue-500",
              step.status === 'error' && "bg-red-100 border-red-500",
              step.status === 'pending' && "bg-gray-100 border-gray-300",
            )}>
              {step.status === 'completed' && <Check className="h-4 w-4 text-green-600" />}
              {step.status === 'active' && <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />}
              {step.status === 'error' && <X className="h-4 w-4 text-red-600" />}
              {step.status === 'pending' && <Circle className="h-3 w-3 text-gray-400" />}
            </div>
            {i < steps.length - 1 && (
              <div className={cn(
                "w-0.5 h-6",
                step.status === 'completed' ? "bg-green-300" : "bg-gray-200"
              )} />
            )}
          </div>
          <div className="pb-6">
            <p className={cn(
              "text-sm font-medium",
              step.status === 'completed' && "text-green-700",
              step.status === 'active' && "text-blue-700",
              step.status === 'error' && "text-red-700",
              step.status === 'pending' && "text-gray-400",
            )}>
              {step.label}
            </p>
            {step.result && (
              <p className="text-xs text-muted-foreground mt-0.5">{step.result}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function getDefaultFilters() {
  const now = new Date();
  return {
    unreadOnly: true,
    month: now.getMonth() + 1, // 1-12
    year: now.getFullYear(),
  };
}

export function GmailSyncModal({ open, onOpenChange, onSuccess }: GmailSyncModalProps) {
  const [state, setState] = useState<WizardState>(initialState);
  const [filters, setFilters] = useState(getDefaultFilters);

  useEffect(() => {
    if (open) {
      setState(initialState);
      setFilters(getDefaultFilters());
    }
  }, [open]);

  const isProcessing = ['connecting', 'fetching', 'extracting', 'confirming'].includes(state.step);

  const handleClose = () => {
    if (isProcessing) return;
    onOpenChange(false);
  };

  const startSync = async () => {
    setState(s => ({ ...s, step: 'connecting', errorMessage: null }));

    try {
      // Step 1: Connect
      const testResult = await api.testGmailConnection();
      setState(s => ({ ...s, connectedEmail: testResult.email || null, step: 'fetching' }));

      // Step 2: Fetch emails with filters
      const fetchResult = await api.gmailFetchEmails({
        unread_only: filters.unreadOnly,
        month: filters.month,
        year: filters.year,
      });
      setState(s => ({
        ...s,
        emails: fetchResult.emails,
        emailCount: fetchResult.count,
      }));

      if (fetchResult.count === 0) {
        setState(s => ({ ...s, step: 'done' }));
        return;
      }

      // Step 3: Extract
      setState(s => ({ ...s, step: 'extracting' }));
      const emailIds = fetchResult.emails.map(e => e.id);
      const extractResult = await api.gmailExtract(emailIds);

      const invoices = extractResult.invoices;
      let detectedDuplicates: Record<string, ExistingInvoiceInfo> = {};
      let duplicateActions: Record<string, DuplicateAction> = {};

      if (invoices.length > 0) {
        // Verificar duplicados contra la BD
        try {
          const checkResult = await api.gmailCheckDuplicates(invoices.map(inv => inv.numero));
          detectedDuplicates = checkResult.duplicates || {};
          // Por defecto, ignorar duplicados
          for (const numero of Object.keys(detectedDuplicates)) {
            duplicateActions[numero] = 'ignore';
          }
        } catch {
          // Si falla el check, continuar sin marcar duplicados
        }
      }

      setState(s => ({
        ...s,
        attachmentsCount: extractResult.attachments_count,
        invoices,
        extractErrors: extractResult.errors,
        selectedIndices: new Set(invoices.map((_, i) => i)),
        detectedDuplicates,
        duplicateActions,
        step: invoices.length > 0 ? 'review' : 'done',
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        step: 'error',
        errorMessage: error instanceof Error ? error.message : 'Error desconocido',
      }));
    }
  };

  const confirmImport = async () => {
    setState(s => ({ ...s, step: 'confirming' }));

    try {
      const selectedInvoices = state.invoices.filter((_, i) => state.selectedIndices.has(i));
      // Filtrar: excluir duplicados marcados como "ignorar"
      const invoicesToSend = selectedInvoices.filter(inv => {
        const isDup = inv.numero in state.detectedDuplicates;
        if (!isDup) return true;
        return state.duplicateActions[inv.numero] === 'replace';
      });
      // Numeros que el usuario eligio reemplazar
      const replaceNumeros = invoicesToSend
        .filter(inv => inv.numero in state.detectedDuplicates && state.duplicateActions[inv.numero] === 'replace')
        .map(inv => inv.numero);

      const allEmailIds = [...new Set(state.emails.map(e => e.id))];

      const result = await api.gmailConfirm({
        invoices: invoicesToSend,
        mark_as_read: state.markAsRead,
        email_ids: state.markAsRead ? allEmailIds : [],
        replace_numeros: replaceNumeros.length > 0 ? replaceNumeros : undefined,
      });

      setState(s => ({
        ...s,
        created: result.created,
        duplicates: result.duplicates,
        confirmErrors: result.errors,
        step: 'done',
      }));

      if (result.created.length > 0) {
        onSuccess();
      }
    } catch (error) {
      setState(s => ({
        ...s,
        step: 'error',
        errorMessage: error instanceof Error ? error.message : 'Error desconocido',
      }));
    }
  };

  const toggleInvoice = (index: number) => {
    setState(s => {
      const newSet = new Set(s.selectedIndices);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return { ...s, selectedIndices: newSet };
    });
  };

  const toggleAll = () => {
    setState(s => {
      const allSelected = s.selectedIndices.size === s.invoices.length;
      return {
        ...s,
        selectedIndices: allSelected ? new Set<number>() : new Set(s.invoices.map((_, i) => i)),
      };
    });
  };

  const steps = buildSteps(state);
  const isWide = state.step === 'review' || (state.step === 'done' && state.created.length > 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={cn("sm:max-w-lg transition-all", isWide && "sm:max-w-3xl")}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Sincronizar Gmail
          </DialogTitle>
          <DialogDescription>
            Importar facturas desde correos electronicos con adjuntos XML
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <WizardStepIndicator steps={steps} />

          {/* Idle: filters + start */}
          {state.step === 'idle' && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium mb-1.5 block">Mes</label>
                  <Select
                    value={String(filters.month)}
                    onValueChange={(v) => setFilters(f => ({ ...f, month: Number(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((name, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-28">
                  <label className="text-sm font-medium mb-1.5 block">Ano</label>
                  <Select
                    value={String(filters.year)}
                    onValueChange={(v) => setFilters(f => ({ ...f, year: Number(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2].map(offset => {
                        const y = new Date().getFullYear() - offset;
                        return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={filters.unreadOnly}
                  onChange={() => setFilters(f => ({ ...f, unreadOnly: !f.unreadOnly }))}
                />
                <span className="text-sm">Solo correos no leidos</span>
              </label>
              <p className="text-xs text-muted-foreground">
                Se buscaran correos {filters.unreadOnly ? 'no leidos ' : ''}de {MONTH_NAMES[filters.month - 1]} {filters.year} con adjuntos XML/ZIP.
              </p>
            </div>
          )}

          {/* Review: invoice selection table */}
          {state.step === 'review' && (
            <div className="space-y-4">
              <p className="text-sm font-medium">
                Facturas encontradas ({state.invoices.length}):
                {Object.keys(state.detectedDuplicates).length > 0 && (
                  <span className="text-yellow-600 font-normal ml-2">
                    ({Object.keys(state.detectedDuplicates).length} duplicada(s))
                  </span>
                )}
              </p>
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={state.selectedIndices.size === state.invoices.length}
                          indeterminate={state.selectedIndices.size > 0 && state.selectedIndices.size < state.invoices.length}
                          onChange={toggleAll}
                        />
                      </TableHead>
                      <TableHead>Numero</TableHead>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="w-28 text-center">Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {state.invoices.map((inv, i) => {
                      const dupInfo = state.detectedDuplicates[inv.numero];
                      const isDuplicate = !!dupInfo;
                      const action = state.duplicateActions[inv.numero];
                      return (
                        <TableRow key={i} className={isDuplicate ? 'bg-yellow-50' : undefined}>
                          <TableCell>
                            <Checkbox
                              checked={state.selectedIndices.has(i)}
                              onChange={() => toggleInvoice(i)}
                            />
                          </TableCell>
                          <TableCell className="font-mono text-xs">{inv.numero}</TableCell>
                          <TableCell className="text-sm">{inv.emisor_razon_social}</TableCell>
                          <TableCell className="text-sm">{inv.fecha_emision}</TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            ${inv.valor_total.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-center">
                            {isDuplicate ? (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => setState(s => ({
                                    ...s,
                                    duplicateActions: { ...s.duplicateActions, [inv.numero]: action === 'replace' ? 'ignore' : 'replace' },
                                  }))}
                                  className={cn(
                                    "inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors",
                                    action === 'replace'
                                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                      : "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                                  )}
                                  title={action === 'replace'
                                    ? `Reemplazar factura existente (${dupInfo.estado})`
                                    : `Duplicada - existente: ${dupInfo.estado}, $${dupInfo.valor_total.toLocaleString()}`
                                  }
                                >
                                  {action === 'replace' ? (
                                    <><RefreshCw className="h-3 w-3" /> Reemplazar</>
                                  ) : (
                                    <><SkipForward className="h-3 w-3" /> Ignorar</>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-xs">Nueva</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {state.extractErrors.length > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-yellow-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                  {state.extractErrors.length} archivo(s) con errores de procesamiento
                </div>
              )}

              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={state.markAsRead}
                  onChange={() => setState(s => ({ ...s, markAsRead: !s.markAsRead }))}
                />
                <span className="text-sm">Marcar correos como leidos en Gmail</span>
              </label>
            </div>
          )}

          {/* Done: summary */}
          {state.step === 'done' && (
            <div className="py-2">
              {state.emailCount === 0 && (
                <div className="text-center py-4">
                  <Mail className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No hay correos nuevos con adjuntos</p>
                </div>
              )}

              {state.emailCount > 0 && state.invoices.length === 0 && (
                <div className="text-center py-4">
                  <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No se encontraron facturas XML en los correos</p>
                </div>
              )}

              {state.created.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="success">{state.created.length}</Badge>
                  <span className="text-sm">factura(s) importada(s) correctamente</span>
                </div>
              )}
              {state.duplicates.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="warning">{state.duplicates.length}</Badge>
                  <span className="text-sm">factura(s) duplicada(s) (ya existian)</span>
                </div>
              )}
              {state.confirmErrors.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="destructive">{state.confirmErrors.length}</Badge>
                  <span className="text-sm">error(es) al importar</span>
                </div>
              )}
              {state.markAsRead && (state.created.length > 0 || state.duplicates.length > 0) && (
                <p className="text-xs text-muted-foreground mt-3">
                  Los correos fueron marcados como leidos en Gmail
                </p>
              )}
            </div>
          )}

          {/* Error */}
          {state.step === 'error' && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error en la sincronizacion</p>
                <p className="text-xs text-red-600 mt-1">{state.errorMessage}</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {state.step === 'idle' && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button onClick={startSync}>
                Iniciar Sincronizacion
              </Button>
            </>
          )}
          {state.step === 'review' && (
            <>
              <Button variant="outline" onClick={handleClose}>Cancelar</Button>
              <Button
                onClick={confirmImport}
                disabled={state.selectedIndices.size === 0}
              >
                Importar {state.selectedIndices.size} Seleccionada(s)
              </Button>
            </>
          )}
          {(state.step === 'done' || state.step === 'error') && (
            <Button onClick={handleClose}>Cerrar</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
